import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { DepartmentalWashoutTableFragment } from "../../../fragments/departmentalManagers";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { IServiceDepartmentalWashout } from "@/Washouts/types/Api/ServiceDepartmental";

// Common fragments
import {
	AddedCompensationFragment,
	CallActivityPerformanceFragment,
	CompensationFragment,
	EmployeeFragment,
	LongevityFragment,
	ReviewFragment,
	TechnicianSpiffsFragment,
} from "@/Washouts/api/common/fragments";

type Params = string[];

export async function getServiceDepartmentalWashout(...params: Params) {
	const [locationId, userId, commissionCycleId, payPeriodId, position] = params;
	const query = gql`
		query getServiceDepartmentalWashout(
			$locationId: String!
			$userId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$position: String!
		) {
			getServiceDepartmentalWashout(
				locationId: $locationId
				userId: $userId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				position: $position
			) {
				_id
				userId
				commissionCycleId
				payPeriodId
				created
				employee {
					...Employee
				}
				serviceDepartment {
					...DepartmentalWashoutTable
				}
				bodyShopDepartment {
					...DepartmentalWashoutTable
				}
				partsDepartment {
					...DepartmentalWashoutTable
				}
				fixedOperationsCosts {
					...DepartmentalWashoutTable
				}
				longevity {
					...Longevity
				}
				callActivityPerformance {
					...WashoutCallActivityPerformance
				}
				review {
					...Review
				}
				addedCompensations {
					...AddedCompensation
				}
				spiffs {
					...Spiffs
				}
				summary {
					...Compensation
				}
			}
		}
		${DepartmentalWashoutTableFragment}
		${AddedCompensationFragment}
		${CallActivityPerformanceFragment}
		${CompensationFragment}
		${EmployeeFragment}
		${LongevityFragment}
		${ReviewFragment}
		${TechnicianSpiffsFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		userId,
		payPeriodId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getServiceDepartmentalWashout", IServiceDepartmentalWashout> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getServiceDepartmentalWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function getServiceDepartmentalWashoutReview(...params: Params) {
	const [locationId, commissionCycleId, payPeriodId = null, position] = params;
	const query = gql`
		query getServiceDepartmentalWashout($locationId: String!, $commissionCycleId: String!, $payPeriodId: String, $position: String!) {
			getServiceDepartmentalWashout(
				commissionCycleId: $commissionCycleId
				locationId: $locationId
				payPeriodId: $payPeriodId
				position: $position
			) {
				_id
				review {
					...Review
				}
			}
		}
		${ReviewFragment}
	`;
	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<
			"getServiceDepartmentalWashout",
			Pick<IServiceDepartmentalWashout, "_id" | "review">
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getServiceDepartmentalWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}
